import React, { useState } from 'react';

import {
  Box,
  Inline,
  IBoxProps,
} from '@styleseat/ui';
import { colors } from '@styleseat/brand';

import {
  Button,
  BodyText,
  Stack,
  HeaderText,
  ModalTrigger,
} from '../../../ui';

import {
  BasicUserIcon,
  BasicCirclePlusIcon,
  SecurityLockIcon,
} from '../../../ui/Icons';

import GetMoreClientsModal from './GetMoreClientsModal';
import ClientExperienceModal from './ClientExperience/ClientExperienceModal';
import useMount from '../../../../hooks/useMount';
import analytics from '../../../../modules/analytics';
import { useLinkTo } from '../../../../hooks';

type EmptyClientListProps = {
  providerId: number;
  style?: IBoxProps['style'],
  bulkImportClick: () => void;
  addClientClick: () => void;
  launchEmail: () => void;
};

const EmptyClientList: React.FC<EmptyClientListProps> = ({
  bulkImportClick,
  addClientClick,
  launchEmail,
  providerId,
  style,
}) => {
  const linkTo = useLinkTo();
  const [showClientExperienceModal, setShowClientExperienceModal] = useState(
    false,
  );
  const handleShowClientExperienceModalClick = () => {
    setShowClientExperienceModal(true);
  };

  const handleCloseClientExperienceModal = () => {
    setShowClientExperienceModal(false);
  };

  useMount(() => {
    analytics.track('pro_clients_whatclientssee_view', { providerId });
    analytics.track('pro_clients_getmoreclients_view', { providerId });
  });

  const headlineText = 'Add your clients and invite\nthem to book an appointment';
  return (
    <Box paddingX={20} alignItems="center" style={style}>
      <Stack
        space={12}
        paddingY={84}
        justifyContent="center"
        maxWidth={420}
      >
        <HeaderText level={2} textAlign="center" style={{ marginBottom: 12 }}>
          {headlineText}
        </HeaderText>
        <Button
          testID="bulk-import-clients"
          onClick={bulkImportClick}
          appearance="primary-neue"
        >
          <Inline
            flex={1}
            space={8}
            justifyContent="center"
            alignItems="center"
            paddingY={10}
          >
            <BasicUserIcon width={15} height={15} color={colors.whiteWash} />
            <BodyText
              whiteSpace="nowrap"
              color={colors.whiteWash}
              weight="bold"
            >
              Add Clients From Contacts
            </BodyText>
          </Inline>
        </Button>
        <Button
          testID="import-client"
          appearance="outline-thin"
          onClick={addClientClick}
        >
          <Inline
            flex={1}
            space={8}
            justifyContent="center"
            alignItems="center"
            paddingY={10}
          >
            <BasicCirclePlusIcon
              width={15}
              height={15}
              color={colors.blackInBlack}
            />
            <BodyText
              whiteSpace="nowrap"
              color={colors.blackInBlack}
              weight="bold"
            >
              Add New Client
            </BodyText>
          </Inline>
        </Button>
        <Inline space={8} paddingY={20}>
          <Stack
            flex={1}
            borderRadius={2}
            paddingY={20}
            paddingX={12}
            color={colors.chill}
            space={4}
          >
            <HeaderText level={4} textAlign="center">
              Learn how to gain more clients
            </HeaderText>
            <ModalTrigger
              renderModal={close => (
                <GetMoreClientsModal close={close} providerId={providerId} linkTo={linkTo} />
              )}
              renderTrigger={open => (
                <Button
                  testID="get-more-clients"
                  appearance="link-lowercase"
                  size="medium"
                  onClick={open}
                  style={{
                    color: colors.blue,
                    borderBottomColor: colors.blue,
                    height: 'auto',
                  }}
                  clickTracking="pro_clients_getmoreclients_click"
                >
                  Get More Clients
                </Button>
              )}
            />
          </Stack>
          <Stack
            flex={1}
            borderRadius={2}
            paddingY={20}
            paddingX={12}
            color={colors.itsOff}
            space={4}
          >
            <HeaderText level={4} textAlign="center">
              What do my clients see when they book?
            </HeaderText>
            <Button
              appearance="link-lowercase"
              size="medium"
              onClick={handleShowClientExperienceModalClick}
              clickTracking="pro_clients_showme_click"
              style={{
                borderBottomColor: colors.smudge,
                color: colors.smudge,
                height: 'auto',
              }}
            >
              Show Me
            </Button>
          </Stack>
        </Inline>
        <Stack space={24} style={{ marginTop: 95 }}>
          <Inline space={4} alignItems="center" justifyContent="center">
            <SecurityLockIcon color={colors.slate} />
            <BodyText
              appearance="smaller"
              color={colors.slate}
              style={{ fontWeight: '700' }}
            >
              Client information is private to only you.
            </BodyText>
          </Inline>
          <Stack>
            <BodyText appearance="small" textAlign="center" color={colors.ash}>
              Need help importing your client list? We can help.
            </BodyText>
            <Inline space={4} justifyContent="center">
              <BodyText appearance="small" color={colors.ash}>
                Send your client list
              </BodyText>
              <Button
                testID="import-client-list-email"
                appearance="link-lowercase"
                size="small"
                style={{
                  height: 'auto',
                  margin: 0,
                  fontWeight: 400,
                  fontSize: 12,
                  lineHeight: '14px',
                  color: colors.ash,
                  borderBottomColor: colors.ash,
                }}
                onClick={() => launchEmail()}
              >
                here
              </Button>
            </Inline>
          </Stack>
        </Stack>
      </Stack>
      {showClientExperienceModal && (
        <ClientExperienceModal
          providerId={providerId}
          onClose={handleCloseClientExperienceModal}
          openBulkImportModal={bulkImportClick}
          openAddClientModal={addClientClick}
        />
      )}
    </Box>
  );
};

export default EmptyClientList;
